import { AxiosError } from "axios";
import { UseQueryOptions, useQuery, useQueries } from "react-query";
import { IPermission, IRole, IRolePermission, Route } from "@edgetier/watchtower-types";
import { getAllPaginatedItems } from "utilities/get-all-paginated-items";

/**
 * Request permissions for a specific role
 * @param url       Start URL to get roles.
 * @param roleId    User role id
 */
const getPermissions = async (url: string) => {
    const permissions = await getAllPaginatedItems<IPermission>(url);
    return permissions;
};

/**
 * Request permissions for a specific role
 * @param url       Start URL to get roles.
 * @param roleId    User role id
 */
const getRolePermissions = async (url: string, roleId: string) => {
    const permissions = await getAllPaginatedItems<IPermission>(url);
    return { [roleId]: permissions };
};

/**
 * Hook to get all permissions for a specific role
 * @param roleId
 * @returns React Query result.
 */
export const useRolePermissions = (
    roleId: string,
    configuration: UseQueryOptions<IPermission[], AxiosError<void>> = {}
) => {
    const url = Route.RolePermissions.replace(":roleId", roleId);

    return useQuery<IPermission[], AxiosError<void>>([url], () => getPermissions(url), {
        ...configuration,
    });
};

/**
 * Request all roles permissions. Loop though all roles and retrieve permissions
 * @param roles     List of roles
 */
export const useAllRolesPermissions = (
    roles: IRole[],
    configuration: UseQueryOptions<IRolePermission, AxiosError<void>> = {}
) => {
    return useQueries(
        roles.map((role) => {
            const url = Route.RolePermissions.replace(":roleId", role.roleId);
            return {
                queryKey: [url, role.roleId],
                queryFn: () => getRolePermissions(url, role.roleId),
                ...configuration,
            };
        })
    );
};
